import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { CoachModal } from '@/features/coach/components/CoachModal';
import { FeedbackTextComposer } from '@/features/feedback-authoring/components/FeedbackTextComposer';
import { FeedbackDraftResume } from '@/features/feedback-authoring/components/FeedbackDraftResume';
import { FeedbackResourceDestination } from '@/features/feedback-authoring/components/FeedbackResourceDestination';
import { useComposerDraftContext, isCurrentDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import { continueFeedbackWorkspace, loadFeedbackContinuation, rememberFeedbackWorkspace } from '@/lib/feedbackAuthoringContinuation';
import type { FeedbackResourceCreation, FeedbackResourceLinkTarget } from '@/features/feedback-authoring/resourceCreationContract';
import { getFeedbackDraft, captureRecapBoundary } from '@/lib/api';
import { refreshFeedbackCollections } from '@/lib/queryClient';
import type { FeedbackConversationTarget } from '@/lib/types';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';
import { resolveSavedPrivateFeedback, type SavedPrivateFeedbackRequest } from './resolveSavedPrivateFeedback';
import { resolvePublishedAssistFeedback, type SavedPublicFeedbackRequest } from './resolvePublishedAssistFeedback';
import { resolveFeedbackFocus, type FeedbackFocusRequest } from './resolveFeedbackFocus';
type SavedAssistRequest = SavedPrivateFeedbackRequest | SavedPublicFeedbackRequest | FeedbackFocusRequest;
function resolveSavedAssist(request: SavedAssistRequest, signal: AbortSignal) {
  if ('feedbackId' in request) return resolveFeedbackFocus(request, signal);
  const accepted = request.accepted;
  return accepted.schema_version === 'coaching-feedback-accepted.v1'
    ? resolvePublishedAssistFeedback({ owner: request.owner, accepted }, signal)
    : resolveSavedPrivateFeedback({ owner: request.owner, accepted }, signal);
}

type Entry = { id: string; mountId: string; context: DraftContext; boundary: ReturnType<typeof captureRecapBoundary>; playerId?: string; draftId?: string; workspaceId?: string;
  creating?: boolean; error?: string; command?: ReturnType<typeof continueFeedbackWorkspace> };
export type FeedbackAuthoringHostHandle = {
  open(playerId?: string): void;
  resume(feedbackId: string): void;
  edit(input: { target: FeedbackConversationTarget; feedbackVersion: number }): void;
  openSavedPrivate(request: SavedPrivateFeedbackRequest, signal: AbortSignal, beforeOpen: () => void): Promise<void>;
  openPublishedAssist(request: SavedPublicFeedbackRequest, signal: AbortSignal, beforeOpen: () => void): Promise<void>;
  openFeedbackFocus(request: FeedbackFocusRequest, signal: AbortSignal, beforeOpen: () => void): Promise<void>;
  suspend():void;
  isEntryActive(entryId:string):boolean;
  activateEntry(entryId:string,onReady?:(target:FeedbackResourceLinkTarget)=>void,onCancel?:()=>void):boolean;
  startEntry(entryId:string,onReady:(target:FeedbackResourceLinkTarget)=>void,onCancel?:()=>void):void;
  chooseResourceDestination(onChosen: (entryId: string, activate: (onReady: (target: FeedbackResourceLinkTarget) => void, onCancel?: () => void) => void) => void, onCancel: () => void): void;
};

/** User events initiate work. Rendering, resuming a known entry and hiding a
 * dialog do not dispatch writes or recreate accepted command identities. */
export const FeedbackAuthoringHost = forwardRef<FeedbackAuthoringHostHandle, {
  players: readonly { id: string; label: string }[];
  onActiveChange(active: boolean): void;
  onCreateResource?:FeedbackResourceCreation;
}>(function FeedbackAuthoringHost({ players, onActiveChange, onCreateResource }, ref) {
  const context = useComposerDraftContext();
  const [entries, setEntries] = useState<Entry[]>([]), [activeId, setActiveId] = useState<string | null>(null);
  const [resumeError, setResumeError] = useState<string>();
  const [resume, setResume] = useState(false), entryPlayer = useRef<string>();
  const [destinationChoice, setDestinationChoice] = useState<'kept' | 'saved' | null>(null);
  const destination = useRef<{ chosen: Parameters<FeedbackAuthoringHostHandle['chooseResourceDestination']>[0]; cancel(): void } | null>(null);
  const destinationActivations = useRef(new Set<Entry['boundary']>());
  const linkTargets=useRef(new Map<string,FeedbackResourceLinkTarget>());
  const registrationCallbacks=useRef(new Map<string,(target:FeedbackResourceLinkTarget|null)=>void>());
  const [registrationVersion,setRegistrationVersion]=useState(0);
  const waiting=useRef<{entryId:string;ready:(target:FeedbackResourceLinkTarget)=>void;cancel?:()=>void}|null>(null);
  const register=(entry:Entry)=>{
    let callback=registrationCallbacks.current.get(entry.mountId);
    if(!callback){callback=target=>{
      if(target){if(linkTargets.current.get(entry.id)===target)return;linkTargets.current.set(entry.id,target);}
      else linkTargets.current.delete(entry.id);
      setRegistrationVersion(value=>value+1);
    };registrationCallbacks.current.set(entry.mountId,callback);}
    return callback;
  };
  const [editChoice,setEditChoice] = useState<{entry: Entry; version: number;
    savedRequest?: SavedAssistRequest; reading?: boolean; error?: string} | null>(null);
  const live = useRef(context); live.current = context;
  const ownedEntries = useRef(entries); ownedEntries.current = entries;
  const foreground = useRef<Entry['boundary'] | null>(null);
  const chooserBoundary = useRef<Entry['boundary'] | null>(null);
  const activeCallback = useRef(onActiveChange); activeCallback.current = onActiveChange;
  const savedReads = useRef(new Set<AbortController>());
  const cancelSavedReads = () => { savedReads.current.forEach(abort => abort.abort()); savedReads.current.clear(); };
  const releaseChooser = () => { chooserBoundary.current?.dispose(); chooserBoundary.current = null; };
  const cancelWaiting = () => { const prior=waiting.current;waiting.current=null;prior?.cancel?.(); };
  const close = () => {
    cancelSavedReads();
    destinationActivations.current.forEach(boundary => boundary.dispose()); destinationActivations.current.clear();
    const choice = destination.current; destination.current = null; setDestinationChoice(null); choice?.cancel();
    cancelWaiting();
    foreground.current = null; releaseChooser();
    setEditChoice(null); setResume(false); setActiveId(null); activeCallback.current(false);
  };
  const closeRef = useRef(close); closeRef.current = close;
  useEffect(() => {
    // Observe the departure itself, including a leave-and-return batched into
    // one React render. Expired controllers must never hold the host open.
    const observe = () => { if (foreground.current && !foreground.current.current()) closeRef.current(); };
    const authOff = useAuthStore.subscribe(observe), scopeOff = useScopeStore.subscribe(observe);
    return () => {
      authOff(); scopeOff(); cancelWaiting(); destination.current?.cancel(); destination.current = null; chooserBoundary.current?.dispose();
      cancelSavedReads();
      destinationActivations.current.forEach(boundary => boundary.dispose()); destinationActivations.current.clear();
      ownedEntries.current.forEach(entry => entry.boundary.dispose());
      activeCallback.current(false);
    };
  }, []);
  const update = (entry: Entry, patch: Partial<Entry>) => setEntries(rows => rows.map(row => row.mountId === entry.mountId ? { ...row, ...patch } : row));
  const show = (entry: Entry) => {
    cancelSavedReads();
    releaseChooser(); foreground.current = entry.boundary;
    setEditChoice(null); setResume(false); setActiveId(entry.id); activeCallback.current(true);
  };
  async function execute(entry: Entry) {
    if (!entry.boundary.current() || !entry.command) return;
    update(entry, { creating: true, error: undefined });
    try {
      const result = await entry.command.send();
      if (!entry.boundary.current()) return;
      if (result.schema_version !== 'feedback-workspace-result.v1') throw new Error('Unexpected workspace result');
      update(entry, { workspaceId: result.workspace_id, creating: false, command: undefined });
    } catch {
      if (entry.boundary.current()) update(entry, { creating: false,
        error: 'The draft could not be confirmed. Check the original operation before continuing.' });
    }
  }
  async function start(playerId?: string, draftId?: string, version?: number,entryId?:string) {
    cancelSavedReads();
    const captured = live.current;
    if (!captured || !isCurrentDraftContext(captured)) return;
    const entry: Entry = { id: entryId??crypto.randomUUID(), mountId: crypto.randomUUID(), context: captured, boundary: captureRecapBoundary({...captured,view:'coach'}), playerId, draftId, creating: true };
    setEntries(rows => [...rows, entry]); show(entry);
    try {
      const base = draftId && version === undefined ? (await getFeedbackDraft(draftId, { scope: captured })).version : version ?? 0;
      if (!entry.boundary.current()) return;
      entry.command = continueFeedbackWorkspace(captured, entry.id, { action: 'create', body: {
        schema_version: 'feedback-workspace-create.v1', operation_id: crypto.randomUUID(),
        feedback_id: draftId ?? null, expected_feedback_version: base, entry_player_id: playerId ?? null,
      } });
      update(entry, { command: entry.command }); await execute(entry);
    } catch {
      if (entry.boundary.current()) update(entry, { creating: false,
        error: 'This saved Feedback could not be opened. Close and choose it again; no new writing was sent.' });
    }
  }
  async function openChosenSavedVersion() {
    const choice = editChoice;
    if (!choice || !choice.entry.boundary.current() || choice.reading) return;
    if (!choice.savedRequest) { await start(undefined, choice.entry.draftId, choice.version); return; }
    cancelSavedReads();
    const abort = new AbortController(); savedReads.current.add(abort);
    setEditChoice({ ...choice, reading: true, error: undefined });
    try {
      // The earlier receipt click only selected this same parent. A later
      // explicit "Open saved version" must obtain its then-current CAS.
      const current = await resolveSavedAssist(choice.savedRequest, abort.signal);
      if (abort.signal.aborted || !savedReads.current.has(abort) || !choice.entry.boundary.current()) return;
      savedReads.current.delete(abort);
      await start(undefined, current.feedbackId, current.feedbackVersion);
    } catch {
      if (!abort.signal.aborted && savedReads.current.has(abort) && choice.entry.boundary.current())
        setEditChoice({ ...choice, error: 'The current saved Feedback could not be read. Your kept writing is unchanged. Retry opening the saved version.' });
    } finally { savedReads.current.delete(abort); }
  }
  useEffect(()=>{
    const request=waiting.current,target=request&&linkTargets.current.get(request.entryId);
    // Admission's layout effect has committed the active state before this
    // passive effect. Registration alone never invokes a Resource callback.
    if(request&&target&&activeId===request.entryId){
      if(target.signal.aborted){cancelWaiting();return;}
      waiting.current=null;request.ready(target);
    }
  },[activeId,registrationVersion]);
  function activateEntry(entryId: string, onReady?: (target: FeedbackResourceLinkTarget) => void, onCancel?: () => void) {
    cancelSavedReads();
    cancelWaiting();
    const captured = live.current; if (!captured || !isCurrentDraftContext(captured)) return false;
    let entry = ownedEntries.current.find(row => row.id === entryId && row.boundary.current());
    // A server-only admission denial can retire this form's target while the
    // client scope still looks valid. Never wait forever or revive that target.
    if (entry && linkTargets.current.get(entryId)?.signal.aborted) return false;
    if (!entry) {
      const stored = loadFeedbackContinuation(captured, entryId); if (!stored) return false;
      entry = { id: entryId, mountId: crypto.randomUUID(), context: captured, boundary: captureRecapBoundary({ ...captured, view: 'coach' }),
        workspaceId: stored.workspace_id ?? undefined, draftId: stored.feedback_id ?? undefined };
      setEntries(rows => [...rows.filter(row => row.id !== entryId), entry!]);
    }
    waiting.current = onReady ? { entryId, ready: onReady, cancel: onCancel } : null; show(entry); setRegistrationVersion(value => value + 1); return true;
  }
  function chooseDestination(kind: 'kept' | 'new' | 'draft', id?: string) {
    const request = destination.current, captured = live.current;
    if (!request || !captured || !isCurrentDraftContext(captured) || !chooserBoundary.current?.current()) return;
    const entryId = kind === 'kept' ? id! : crypto.randomUUID();
    const admission = captureRecapBoundary({ ...captured, view: 'coach' }); destinationActivations.current.add(admission);
    destination.current = null; setDestinationChoice(null); foreground.current = null; releaseChooser(); activeCallback.current(false);
    request.chosen(entryId, (ready, cancel) => {
      const allowed = admission.current(); admission.dispose(); destinationActivations.current.delete(admission);
      if (!allowed || !isCurrentDraftContext(captured)) { cancel?.(); return; }
      if (kind === 'kept') { if (!activateEntry(entryId, ready, cancel)) cancel?.(); }
      else { cancelWaiting(); waiting.current = { entryId, ready, cancel }; void start(undefined, kind === 'draft' ? id : undefined, undefined, entryId); }
    });
  }
  async function openSavedAssist(request: SavedAssistRequest, signal: AbortSignal, beforeOpen: () => void) {
      cancelSavedReads();
      if (signal.aborted || !isCurrentDraftContext(request.owner))
        throw new DOMException('Saved Feedback context changed', 'AbortError');
      const abort = new AbortController(), cancel = () => abort.abort();
      const boundary = captureRecapBoundary({ ...request.owner, view: 'coach' });
      signal.addEventListener('abort', cancel, { once: true }); savedReads.current.add(abort);
      try {
        const current = await resolveSavedAssist(request, abort.signal);
        if (signal.aborted || abort.signal.aborted || !savedReads.current.has(abort) || !boundary.current())
          throw new DOMException('Saved Feedback context changed', 'AbortError');
        savedReads.current.delete(abort);
        const kept = ownedEntries.current.slice().reverse().find(entry => entry.draftId === current.feedbackId && entry.boundary.current());
        beforeOpen();
        if (signal.aborted || !boundary.current())
          throw new DOMException('Saved Feedback context changed', 'AbortError');
        if (kept) {
          releaseChooser(); foreground.current = kept.boundary; setResume(false); setActiveId(null);
          setEditChoice({ entry: kept, version: current.feedbackVersion, savedRequest: structuredClone(request) }); activeCallback.current(true);
        } else await start(undefined, current.feedbackId, current.feedbackVersion);
      } finally {
        signal.removeEventListener('abort', cancel); savedReads.current.delete(abort); boundary.dispose();
      }
  }
  useImperativeHandle(ref, () => ({
    suspend(){close();},
    isEntryActive(entryId) {
      const entry = ownedEntries.current.find(value => value.id === entryId && value.boundary === foreground.current);
      return !!entry && entry.boundary.current() && activeId === entryId;
    },
    activateEntry,
    startEntry(entryId,onReady,onCancel){cancelWaiting();waiting.current={entryId,ready:onReady,cancel:onCancel};void start(undefined,undefined,undefined,entryId);},
    chooseResourceDestination(chosen, cancel) {
      close(); const captured = live.current; if (!captured || !isCurrentDraftContext(captured)) { cancel(); return; }
      chooserBoundary.current = captureRecapBoundary({ ...captured, view: 'coach' }); foreground.current = chooserBoundary.current;
      destination.current = { chosen, cancel }; setDestinationChoice('kept'); activeCallback.current(true);
    },
    open(playerId) {
      cancelSavedReads();
      cancelWaiting();
      const captured = live.current;
      if (!captured || !isCurrentDraftContext(captured)) return;
      releaseChooser(); chooserBoundary.current = captureRecapBoundary({...captured,view:'coach'});
      foreground.current = chooserBoundary.current;
      entryPlayer.current = playerId; setEditChoice(null); setActiveId(null); setResume(true); activeCallback.current(true);
    },
    resume(feedbackId) { void start(undefined,feedbackId); },
    openSavedPrivate: openSavedAssist,
    openPublishedAssist: openSavedAssist,
    openFeedbackFocus: openSavedAssist,
    edit({ target, feedbackVersion }) {
      cancelSavedReads();
      const kept = entries.slice().reverse().find(entry=>entry.draftId===target.feedbackId && entry.boundary.current());
      if (kept) { releaseChooser(); foreground.current = kept.boundary; setResume(false); setActiveId(null); setEditChoice({entry:kept,version:feedbackVersion}); activeCallback.current(true); }
      else void start(undefined, target.feedbackId, feedbackVersion);
    },
  }));
  const refresh = (entry: Entry) => { if (entry.boundary.current()) void refreshFeedbackCollections(entry.context); };
  return <>
    {resumeError && <CoachModal title="Resume draft" onClose={() => setResumeError(undefined)}><p role="alert">{resumeError}</p><Button onClick={() => setResumeError(undefined)}>Back to drafts</Button></CoachModal>}
    {context && destinationChoice === 'kept' && <FeedbackResourceDestination
      kept={entries.filter(entry => entry.boundary.current()).map((entry, index) => ({ id: entry.id,
        label: entry.playerId ? `Feedback for ${players.find(player => player.id === entry.playerId)?.label ?? 'selected player'}` : `${entry.draftId ? 'Saved Feedback writing' : 'Draft'} ${index + 1}` }))}
      onKept={id => chooseDestination('kept', id)} onNew={() => chooseDestination('new')} onSaved={() => setDestinationChoice('saved')} onClose={close} />}
    {context && destinationChoice === 'saved' && <FeedbackDraftResume scope={context} onClose={close}
      onNew={() => chooseDestination('new')} onResume={id => chooseDestination('draft', id)} onResumeContinuation={target => {
        if (!isCurrentDraftContext(context)) return;
        try { if (target.workspaceId && !loadFeedbackContinuation(context, target.entryId)) rememberFeedbackWorkspace(context, target.entryId, target.workspaceId); }
        catch { setResumeError('Draft recovery could not be stored in this browser. The Resource and draft are unchanged.'); return; }
        chooseDestination('kept', target.entryId);
      }} />}
    {editChoice && editChoice.entry.boundary.current() && <CoachModal title="Continue Feedback" onClose={close}><p>Continue your kept draft changes, or open the current saved version {editChoice.version} in a separate draft.</p>
      {editChoice.error && <p role="alert">{editChoice.error}</p>}
      <div className="flex flex-wrap gap-2 mt-4"><Button onClick={()=>show(editChoice.entry)}>Continue writing</Button><Button variant="outline" disabled={editChoice.reading} onClick={()=>void openChosenSavedVersion()}>{editChoice.reading ? 'Reading saved Feedback…' : 'Open saved version'}</Button></div></CoachModal>}
    {context && resume && <FeedbackDraftResume scope={context} onClose={close}
      onNew={() => void start(entryPlayer.current)} onResume={id => void start(undefined, id)}
      onResumeContinuation={target => {
        if (!isCurrentDraftContext(context) || !chooserBoundary.current?.current()) return;
        const kept = entries.find(row => row.id === target.entryId);
        if (kept?.boundary.current()) { show(kept); return; }
        // Journal identity survives; the departed mounted controller does not.
        // A distinct React key restores through fresh authority without sending.
        kept?.boundary.dispose();
        const entry: Entry = { id: target.entryId, mountId: crypto.randomUUID(), context,
          boundary: captureRecapBoundary({...context,view:'coach'}),
          workspaceId: target.workspaceId, draftId: target.draftId };
        setEntries(rows => [...rows.filter(row => row.id !== target.entryId), entry]);
        show(entry);
      }} />}
    {entries.map(entry => {
      const active = activeId === entry.id && entry.boundary.current();
      if (entry.creating || entry.error) return <CoachModal key={entry.mountId} active={active} title="Give feedback" onClose={close}>
        <p role={entry.error ? 'alert' : 'status'}>{entry.error ?? 'Opening draft changes…'}</p>
        {entry.error && entry.command && <Button onClick={() => void execute(entry)}>Check original operation</Button>}
      </CoachModal>;
      return <FeedbackTextComposer key={entry.mountId} entryId={entry.id} scope={entry.context}
        onCreateResource={onCreateResource} resourceCreationKinds={onCreateResource?['tactical_board','moment','clip']:[]}
        onResourceLinkTarget={register(entry)}
        workspaceId={entry.workspaceId} draftId={entry.draftId} players={players}
        entryPlayer={players.find(player => player.id === entry.playerId)} active={active}
        onClose={close} onSaved={result => { update(entry,{draftId:result.feedback_id}); refresh(entry); }} onPublished={result => { update(entry,{draftId:result.feedback_id}); refresh(entry); }}
        onDiscarded={() => { refresh(entry); close(); }} />;
    })}
  </>;
});
